/*
  customsirge_home_store_locator_teaser.js
  Purpose:  Decision 0058 §2 — the retailer logo row is a FULL-BLEED, genuinely
            seamless loop. customsirge_motion.js's generic `initMarquee` cannot
            express it, for two measured reasons, so this section owns its own
            marquee driver instead of the shared kit:

            1. WRONG PERIOD (the visible jump). initMarquee appends exactly one
               clone inside the track and animates by `track.scrollWidth / 2`.
               The clone is a flex CHILD of the track, so the track's own
               `column-gap` also sits between the two copies — the repeating
               pattern's period is `setWidth + gap`, not `(2*setWidth + gap)/2`.
               @1440 that is 1141px vs the 1091px it animates by: the row
               snapped back 50px on every wrap.
            2. NOT ENOUGH COPIES (the blank edge). Two copies are 2282px @1440.
               A seamless loop needs `viewport + period` of content so the
               vacated left side is still covered at the end of the cycle;
               2282 < 1440 + 1141, so a 299px empty strip walked in from the
               right edge. This file clones until the track covers both
               `viewport + period` AND the 2x-viewport floor decision 0058 asks
               for (3 copies = 3423px @1440, 3 x 391 = 1173px @420).

            Kept from the shared kit, deliberately: the `data-speed` px/second
            family (`marquee_speed`, default 40), pause-on-hover, the
            nested-<ul>-clone technique (so `.__logo:nth-child(N)` CSS and
            spec.json's per-logo checks keep addressing the 5 real <li>s), and
            reduced motion = no clone, no animation. Under reduced motion the
            row is additionally CENTRED at every width (0058 §2), which is the
            one place this driver departs from the drawn mobile left-alignment.

  Spec:     .claude/knowledge/decisions/0058-teaser-hide-locator-fullbleed-logos.md §2
            (supersedes the drawn logo x-positions — see spec.json
            disputes_resolved[]).
  Owner:    figma-fixer(home_store_locator_teaser), 2026-09-28
*/
(function () {
  'use strict';

  var Customsirge = window.Customsirge;
  if (!Customsirge || typeof Customsirge.register !== 'function') return;

  function reducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function numAttr(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) && n > 0 ? n : fallback;
  }

  function initLogoMarquee(root) {
    var track = root.querySelector('[data-customsirge-marquee-track]');
    if (!track || !track.children.length) return undefined;

    if (reducedMotion()) {
      // No clone, no animation, no custom properties: the CSS animation is
      // gated on the "ready" attribute below, so the row stays the plain
      // static single row. 0058 §2 centres it at every width.
      root.setAttribute('data-customsirge-hslt-marquee-static', 'true');
      return undefined;
    }

    var speed = numAttr(root.dataset.speed, 40); // px/second

    function clearClones() {
      Array.prototype.forEach.call(
        track.querySelectorAll('[data-customsirge-marquee-clone]'),
        function (node) {
          node.remove();
        }
      );
    }

    function build() {
      clearClones();

      // Measured with the originals alone, so this is exactly ONE set.
      var setWidth = track.scrollWidth;
      if (setWidth <= 0) return;

      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap);
      if (!isFinite(gap)) gap = 0;

      // The repeating pattern advances by one set PLUS the gap that separates
      // it from the next copy — uniform, because every copy is a flex child of
      // the same nowrap track.
      var period = setWidth + gap;
      var viewport = window.innerWidth || track.clientWidth || period;

      // The running track is anchored to the LEFT edge and then pulled back by
      // one whole period (`--...-offset` below), so the row already overflows
      // the left edge at t=0 instead of starting flush against it. That costs
      // one period of content on the right, hence `+ 2`:
      //
      //   left edge   starts at -period, ends at -2*period          (always < 0)
      //   right edge  worst case = copies*period - 2*period >= viewport
      //
      // Centring the track instead (the first attempt) splits the overflow
      // evenly, which leaves less than one period of slack on the right: at
      // 1440 it measured a 199px blank strip walking in from the right edge at
      // the end of every cycle. The 2x-viewport floor is decision 0058's own
      // acceptance rule and is kept as a floor on top.
      var copies = Math.max(
        Math.ceil(viewport / period) + 2,
        Math.ceil((2 * viewport) / period)
      );

      var template = track.cloneNode(true);
      template.removeAttribute('data-customsirge-marquee-track');
      template.removeAttribute('id');
      template.setAttribute('aria-hidden', 'true');
      template.setAttribute('data-customsirge-marquee-clone', 'true');
      template.style.animation = 'none';
      Array.prototype.forEach.call(
        template.querySelectorAll('a, button, input, [tabindex]'),
        function (node) {
          node.setAttribute('tabindex', '-1');
        }
      );

      for (var i = 1; i < copies; i += 1) {
        track.appendChild(template.cloneNode(true));
      }

      track.style.setProperty('--customsirge-hslt-marquee-offset', -period + 'px');
      track.style.setProperty('--customsirge-motion-marquee-distance', period + 'px');
      track.style.setProperty(
        '--customsirge-motion-marquee-duration',
        period / speed + 's'
      );
      root.setAttribute('data-customsirge-hslt-marquee-ready', 'true');
    }

    build();

    var onResize = Customsirge.debounce(build, 200);
    window.addEventListener('resize', onResize);
    window.addEventListener('customsirge:relayout', onResize);

    return function cleanup() {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('customsirge:relayout', onResize);
      clearClones();
      track.style.removeProperty('--customsirge-hslt-marquee-offset');
      track.style.removeProperty('--customsirge-motion-marquee-distance');
      track.style.removeProperty('--customsirge-motion-marquee-duration');
      root.removeAttribute('data-customsirge-hslt-marquee-ready');
    };
  }

  Customsirge.register('home_store_locator_teaser', {
    selector: '[data-customsirge-hslt-marquee]',
    init: function (root) {
      var teardown = initLogoMarquee(root);
      return function cleanup() {
        if (typeof teardown === 'function') teardown();
      };
    },
  });
})();
